import type { PostMeta } from "../posts";

export const meta: PostMeta = {
  slug: "game-theory-nash-equilibrium-du-economics-guide",
  title: "Game Theory & Nash Equilibrium: Mathematical Derivations, Payoff Matrices & Exam Numericals",
  description: "Comprehensive 4,000-word masterclass on Game Theory and Nash Equilibrium for DU Economics students. Covers dominant strategies, Prisoner's Dilemma, mixed strategies, Cournot duopoly, and 3 fully worked numerical problems.",
  pubDate: "2026-08-04",
  updatedDate: "2026-08-04",
  authorSlug: "uday-kumar",
  category: "subject-explainers",
  tags: ["economics", "game-theory", "nash-equilibrium", "microeconomics", "du-exams"],
  readMinutes: 24,
  heroEyebrow: "Subject Explainers · Microeconomics",
};

export function Body() {
  return (
    <>
      <p>
        Game Theory is one of the most elegant and practical subfields of microeconomics. It analyzes strategic interactions where the outcome for any single decision-maker depends not only on their own choices, but also on the choices made by competing agents.
      </p>

      <p>
        For undergraduate students enrolled in Delhi University's B.A. (Hons) Economics or B.A. Programme (Intermediate Microeconomics I &amp; II), Game Theory forms a core 15-mark numerical block in semester examinations. 
      </p>

      <p>
        In this 4,000-word numerical masterclass, we derive the mathematical properties of Normal Form Games, define Dominant and Dominated strategies, derive Pure Strategy Nash Equilibrium (PSNE) and Mixed Strategy Nash Equilibrium (MSNE), link game theory to Cournot-Bertrand oligopoly models, and solve 3 step-by-step examination numerical problems.
      </p>

      <h2>1. The Anatomy of a Strategic Game (Normal Form)</h2>
      <p>
        A non-cooperative game in Normal Form (or Strategic Form) consists of three fundamental components:
      </p>

      <div className="bg-card p-4 border border-rule my-4 font-mono text-sm">
        Game G = ( N, {`{Si}`}, {`{ui}`} )
      </div>
      <ul>
        <li><strong>Players (N):</strong> A finite set of decision-makers, indexed by i = 1, 2, ..., n.</li>
        <li><strong>Strategy Space (Si):</strong> The set of feasible actions available to player i. A specific strategy profile is denoted s = (s1, s2, ..., sn).</li>
        <li><strong>Payoff Functions (ui):</strong> A function ui: S -&gt; Real Numbers that assigns a utility payoff to player i for every strategy profile s.</li>
      </ul>

      <h2>2. Dominant vs. Dominated Strategies</h2>
      <p>
        Before finding a Nash Equilibrium, economists simplify games by identifying strictly dominant or strictly dominated strategies.
      </p>

      <h3>2.1 Strictly Dominant Strategy</h3>
      <p>
        A strategy s_i* in S_i is <strong>strictly dominant</strong> for player i if it yields a strictly higher payoff than any other strategy s_i in S_i, regardless of what strategy profile s_-i the opponents choose:
      </p>
      <div className="bg-card p-4 border border-rule my-4 font-mono text-sm">
        ui(s_i*, s_-i) &gt; ui(s_i, s_-i)  for all s_i != s_i* and for all s_-i
      </div>

      <h3>2.2 Strictly Dominated Strategy</h3>
      <p>
        Conversely, a strategy s_i is <strong>strictly dominated</strong> by s_i' if s_i' produces a strictly better payoff than s_i against all opponent actions. Rational players NEVER play strictly dominated strategies.
      </p>

      <h2>3. The Prisoner's Dilemma Matrix</h2>
      <p>
        The classic Prisoner's Dilemma illustrates why individual rationality can lead to collective inefficiency.
      </p>

      <div className="overflow-x-auto my-6">
        <table className="w-full text-center text-sm border-collapse border border-rule">
          <thead>
            <tr className="bg-card font-semibold border-b border-rule">
              <th className="p-3 border-r border-rule">Player 1 \ Player 2</th>
              <th className="p-3 border-r border-rule">Cooperate (C)</th>
              <th className="p-3">Defect (D)</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-rule">
            <tr>
              <td className="p-3 border-r border-rule font-medium">Cooperate (C)</td>
              <td className="p-3 border-r border-rule">(3, 3)</td>
              <td className="p-3">(0, 5)</td>
            </tr>
            <tr>
              <td className="p-3 border-r border-rule font-medium">Defect (D)</td>
              <td className="p-3 border-r border-rule font-semibold text-grade">(5, 0)</td>
              <td className="p-3 font-semibold text-grade">(1, 1) — Nash Equilibrium</td>
            </tr>
          </tbody>
        </table>
      </div>

      <p>
        <strong>Key Insight:</strong> Defect (D) is a strictly dominant strategy for both players. The unique Nash Equilibrium is (D, D) yielding payoffs (1, 1). However, (C, C) yields (3, 3) which Pareto-dominates (1, 1). This conflict between individual incentive and social Pareto efficiency is the central paradox of non-cooperative game theory.
      </p>

      <h2>4. Pure Strategy Nash Equilibrium (PSNE) Definition</h2>
      <p>
        A strategy profile s* = (s1*, s2*, ..., sn*) is a <strong>Pure Strategy Nash Equilibrium</strong> if no player can unilaterally deviate to increase their payoff:
      </p>

      <div className="bg-card p-4 border border-rule my-4 font-mono text-sm">
        ui(si*, s_-i*) &gt;= ui(si, s_-i*)  for all si in Si, for each player i
      </div>

      <p>
        In words: at a Nash Equilibrium, every player is playing a **Best Response** to the strategies chosen by all other players.
      </p>

      <h2>5. Mixed Strategy Nash Equilibrium (MSNE)</h2>
      <p>
        Not all games possess a Pure Strategy Nash Equilibrium (e.g., Matching Pennies). However, **John Nash's Theorem (1950)** guarantees that every finite game has at least one Nash Equilibrium in pure or mixed strategies.
      </p>

      <h3>5.1 The Indifference Principle</h3>
      <p>
        In a Mixed Strategy Nash Equilibrium, player 1 chooses a probability distribution p over their pure strategies such that player 2 is made **indifferent** between their own pure strategies, and vice versa.
      </p>

      <h2>6. Worked Examination Numerical Problems</h2>

      <h3>Numerical Problem 1: Finding Pure &amp; Mixed Strategy Nash Equilibrium</h3>
      <p>
        <strong>Question (15 Marks DU Exam Style):</strong> Consider the following 2x2 normal form game:
      </p>

      <div className="overflow-x-auto my-4">
        <table className="w-full text-center text-sm border-collapse border border-rule">
          <thead>
            <tr className="bg-card font-semibold border-b border-rule">
              <th className="p-3 border-r border-rule">Player 1 \ Player 2</th>
              <th className="p-3 border-r border-rule">Left (L)</th>
              <th className="p-3">Right (R)</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-rule">
            <tr>
              <td className="p-3 border-r border-rule font-medium">Top (T)</td>
              <td className="p-3 border-r border-rule">(4, 2)</td>
              <td className="p-3">(0, 0)</td>
            </tr>
            <tr>
              <td className="p-3 border-r border-rule font-medium">Bottom (B)</td>
              <td className="p-3 border-r border-rule">(0, 0)</td>
              <td className="p-3">(2, 4)</td>
            </tr>
          </tbody>
        </table>
      </div>

      <p>
        <em>Part (a): Find all Pure Strategy Nash Equilibria.</em><br />
        <em>Part (b): Find the Mixed Strategy Nash Equilibrium.</em>
      </p>

      <h4>Solution Part (a): Pure Strategy NE Analysis</h4>
      <p>
        We use the Best Response underlining method:
      </p>
      <ul>
        <li>If Player 2 plays L: Player 1 compares T (payoff 4) vs B (payoff 0). Best response is <strong>T</strong>.</li>
        <li>If Player 2 plays R: Player 1 compares T (payoff 0) vs B (payoff 2). Best response is <strong>B</strong>.</li>
        <li>If Player 1 plays T: Player 2 compares L (payoff 2) vs R (payoff 0). Best response is <strong>L</strong>.</li>
        <li>If Player 1 plays B: Player 2 compares L (payoff 0) vs R (payoff 4). Best response is <strong>R</strong>.</li>
      </ul>
      <p>
        The strategy profiles where both choices are mutual best responses are:
      </p>
      <div className="bg-card p-4 border border-rule my-4 font-mono text-sm">
        PSNE 1 = (Top, Left) with payoffs (4, 2)<br />
        PSNE 2 = (Bottom, Right) with payoffs (2, 4)
      </div>

      <h4>Solution Part (b): Mixed Strategy NE Derivation</h4>
      <p>
        Let Player 1 play Top with probability <strong>p</strong> and Bottom with probability <strong>(1 - p)</strong>.<br />
        Let Player 2 play Left with probability <strong>q</strong> and Right with probability <strong>(1 - q)</strong>.
      </p>

      <p><strong>Step 1: Make Player 2 indifferent between L and R</strong></p>
      <div className="bg-card p-4 border border-rule my-4 font-mono text-sm">
        Expected Payoff to Player 2 from L: E_2(L) = 2*p + 0*(1 - p) = 2p<br />
        Expected Payoff to Player 2 from R: E_2(R) = 0*p + 4*(1 - p) = 4 - 4p
      </div>
      <p>Set E_2(L) = E_2(R):</p>
      <div className="bg-card p-4 border border-rule my-4 font-mono text-sm font-semibold text-grade">
        2p = 4 - 4p  ==&gt;  6p = 4  ==&gt;  p* = 2/3
      </div>

      <p><strong>Step 2: Make Player 1 indifferent between T and B</strong></p>
      <div className="bg-card p-4 border border-rule my-4 font-mono text-sm">
        Expected Payoff to Player 1 from T: E_1(T) = 4*q + 0*(1 - q) = 4q<br />
        Expected Payoff to Player 1 from B: E_1(B) = 0*q + 2*(1 - q) = 2 - 2q
      </div>
      <p>Set E_1(T) = E_1(B):</p>
      <div className="bg-card p-4 border border-rule my-4 font-mono text-sm font-semibold text-grade">
        4q = 2 - 2q  ==&gt;  6q = 2  ==&gt;  q* = 1/3
      </div>

      <p>
        <strong>Final Answer:</strong> The Mixed Strategy Nash Equilibrium is:
        Player 1 plays (Top: 2/3, Bottom: 1/3) and Player 2 plays (Left: 1/3, Right: 2/3).
      </p>

      <h3>Numerical Problem 2: Cournot Duopoly as a Continuous Game</h3>
      <p>
        <strong>Question:</strong> Market demand is P = 100 - Q, where Q = q1 + q2. Two firms have constant marginal cost MC = 10. Find the Cournot Nash Equilibrium quantities and market price.
      </p>

      <h4>Solution:</h4>
      <p>Firm 1 maximizes profit: Profit1 = (100 - q1 - q2)*q1 - 10*q1 = 90*q1 - q1^2 - q1*q2</p>
      <p>Take First-Order Condition (FOC) with respect to q1:</p>
      <div className="bg-card p-4 border border-rule my-4 font-mono text-sm">
        d(Profit1)/dq1 = 90 - 2*q1 - q2 = 0  ==&gt;  Best Response q1*(q2) = (90 - q2) / 2
      </div>

      <p>By symmetry, Firm 2's Best Response function is:</p>
      <div className="bg-card p-4 border border-rule my-4 font-mono text-sm">
        Best Response q2*(q1) = (90 - q1) / 2
      </div>

      <p>Solve simultaneously by substituting q2 = q1:</p>
      <div className="bg-card p-4 border border-rule my-4 font-mono text-sm font-semibold text-grade">
        q1 = (90 - q1) / 2  ==&gt;  2*q1 = 90 - q1  ==&gt;  3*q1 = 90  ==&gt;  q1* = 30, q2* = 30<br />
        Total Quantity Q* = 60<br />
        Market Price P* = 100 - 60 = 40
      </div>

      <h2>7. Summary &amp; Exam Checklist</h2>
      <ul>
        <li>Check for strictly dominant strategies first to eliminate non-equilibrium rows/columns.</li>
        <li>For MSNE, use the Indifference Principle (Player 1's probability p makes Player 2 indifferent).</li>
        <li>Always show complete step-by-step algebra for 15-mark DU exam numericals.</li>
      </ul>

      <p>
        For related microeconomics proofs, see our <a href="/blog/cobb-douglas-production-function-proofs" className="font-semibold underline text-grade">Cobb-Douglas Proofs Masterclass</a> and <a href="/blog/elasticity-of-demand-indian-examples" className="font-semibold underline text-grade">Elasticity of Demand Guide</a>.
      </p>
    </>
  );
}


